import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Fingerprint, KeyRound, X, ArrowRight } from "lucide-react";
import {
  getStoredQuickLogin, forgetQuickLogin, decodeRequestOptions, encodeAssertion, isWebAuthnSupported,
} from "@/lib/quickLogin";

export default function QuickLoginPanel({ onFallback }) {
  const { setUser } = useAuth();
  const [stored, setStored] = useState({ pin_device_id: null, webauthn_device_id: null, email: null, name: null });
  const [mode, setMode] = useState(null); // null | "pin"
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setStored(getStoredQuickLogin()); }, []);

  if (!stored.pin_device_id && !stored.webauthn_device_id) return null;

  const afterLogin = (data) => {
    localStorage.setItem("nb_token", data.token);
    setUser(data.user);
    // Redirect via full reload so AuthContext hydrates properly
    window.location.href = data.user.role === "admin" ? "/admin" : "/dashboard";
  };

  const loginPin = async (e) => {
    e.preventDefault();
    if (pin.length < 4) { toast.error("Enter your PIN"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/quick-login/pin/verify", { device_id: stored.pin_device_id, pin });
      toast.success("Welcome back");
      afterLogin(data);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      toast.error(msg);
      if (err.response?.status === 429) { forgetQuickLogin("pin"); setStored(getStoredQuickLogin()); }
    } finally { setBusy(false); }
  };

  const loginFingerprint = async () => {
    if (!isWebAuthnSupported()) { toast.error("This browser doesn't support fingerprint login"); return; }
    setBusy(true);
    try {
      const { data: opts } = await api.post("/quick-login/webauthn/login-options", { device_id: stored.webauthn_device_id });
      const publicKey = decodeRequestOptions(opts);
      const cred = await navigator.credentials.get({ publicKey });
      const encoded = encodeAssertion(cred);
      const { data } = await api.post("/quick-login/webauthn/login-verify", { device_id: stored.webauthn_device_id, credential: encoded });
      toast.success("Welcome back");
      afterLogin(data);
    } catch (err) {
      if (err?.name === "NotAllowedError") toast.error("Fingerprint prompt cancelled");
      else toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const forgetAll = () => {
    forgetQuickLogin();
    setStored(getStoredQuickLogin());
    toast.info("Quick login removed from this device");
  };

  const initial = (stored.name || stored.email || "?").charAt(0).toUpperCase();

  return (
    <div data-testid="quick-login-panel" className="rounded-2xl border border-nb-cyan/30 bg-nb-cyan/[0.03] p-5 mb-6">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-nb-cyan/20 border border-nb-cyan/40 flex items-center justify-center text-nb-cyan font-bold text-lg">{initial}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-nb-cyan">Welcome back</div>
          <div className="text-white font-heading text-lg truncate">{stored.name || stored.email}</div>
        </div>
        <button data-testid="quick-login-forget" onClick={forgetAll} className="text-slate-500 hover:text-slate-300 p-1" title="Not you? Remove device"><X size={14}/></button>
      </div>

      {mode === "pin" ? (
        <form onSubmit={loginPin} className="mt-4 space-y-3">
          <div className="text-xs text-slate-400 uppercase tracking-widest">Enter your PIN</div>
          <input
            data-testid="quick-login-pin-input"
            type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8} autoFocus
            value={pin} onChange={(e)=>setPin(e.target.value.replace(/\D/g,""))}
            className="nb-input text-center text-2xl tracking-[0.5em] font-mono"
            placeholder="••••"
          />
          <div className="flex gap-2">
            <button type="button" onClick={()=>{ setMode(null); setPin(""); }} className="nb-btn-secondary text-sm">Back</button>
            <button data-testid="quick-login-pin-submit" disabled={busy || pin.length < 4} className="nb-btn-primary text-sm flex-1">{busy?"Unlocking…":<>Unlock <ArrowRight size={14} className="inline ml-1"/></>}</button>
          </div>
        </form>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-2">
          {stored.webauthn_device_id && isWebAuthnSupported() && (
            <button data-testid="quick-login-fingerprint-btn" onClick={loginFingerprint} disabled={busy} className="nb-btn-primary text-sm flex items-center justify-center gap-2">
              <Fingerprint size={18}/> {busy ? "Verifying…" : "Sign in with Fingerprint / Face"}
            </button>
          )}
          {stored.pin_device_id && (
            <button data-testid="quick-login-pin-btn" onClick={()=>setMode("pin")} disabled={busy} className={`${stored.webauthn_device_id ? "nb-btn-secondary" : "nb-btn-primary"} text-sm flex items-center justify-center gap-2`}>
              <KeyRound size={16}/> Sign in with PIN
            </button>
          )}
          <button data-testid="quick-login-password-fallback" onClick={onFallback} className="text-xs text-slate-400 hover:text-white mt-2 text-center">Use email &amp; password instead</button>
        </div>
      )}
    </div>
  );
}
